import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import './FileAttachmentModal.css';
import { formatFileSize, getFileType, getAttachmentIcon } from '../../../../../utils/fileUtils';
import useFileDownload from '../../../../../hooks/useFileDownload';
import MessageReactions from './MessageReactions';
import SmartEmojiPicker from './SmartEmojiPicker';
import ReactionDetailsModal from './ReactionDetailsModal';

const FileAttachmentModal = ({
  message,
  senderName,
  thumbnailUrl,
  filename,
  msgtype,
  fileSize,
  mimeType,
  displayReactions,
  onReplyMessage,
  portalContainer,
  onFocusMessage,
  onToggleReaction,
  onClose,
}) => {
  const [isReactionDetailsOpen, setReactionDetailsOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const { downloadFile } = useFileDownload();
  const reactions = displayReactions || message?._reactions;

  const handleDownloadClick = useCallback(() => {
    downloadFile({ url: message.getContent().url, filename, mimeType });
  }, [downloadFile, message, filename, mimeType]);

  const handleReplyClick = useCallback(() => {
    onClose();
    onReplyMessage(message, senderName, filename, msgtype, filename, thumbnailUrl, fileSize);
  }, [onReplyMessage, onClose, message, senderName, filename, msgtype, thumbnailUrl, fileSize]);

  const handleFocusClick = useCallback(() => {
    onClose();
    onFocusMessage(message);
  }, [onFocusMessage, onClose, message]);

  const handleReactClick = (e) => {
    e.stopPropagation();
    setShowEmojiPicker(prev => !prev);
  };

  // Close emoji picker when clicking outside of it
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const iconSrc = getAttachmentIcon(msgtype, filename, mimeType);
  const fileType = getFileType(mimeType, filename);
  const formattedSize = formatFileSize(fileSize);

  if (!portalContainer) return null;

  return createPortal(
    <div className="file-attachment-modal-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="file-attachment-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* UI controls are positioned relative to the content box */}
        <button className="file-attachment-modal-close-btn" onClick={onClose} aria-label="Close preview">
          <i className="fa fa-times"></i>
        </button>
        <div className="file-attachment-modal-actions">
          <button className="file-attachment-modal-action-btn" title="Download" onClick={handleDownloadClick}>
            <i className="fa fa-cloud-download"></i>
          </button>
          <button className="file-attachment-modal-action-btn" title="Reply" onClick={handleReplyClick}>
            <i className="fa fa-reply"></i>
          </button>
          <button className="file-attachment-modal-action-btn" title="Focus" onClick={handleFocusClick}>
            <i className="fa fa-commenting"></i>
          </button>
          <button className="file-attachment-modal-action-btn" title="React" onClick={handleReactClick}>
            <img src='/images/svg/emoji-react.svg' alt='React' className="file-attachment-modal-react-icon" />
          </button>
        </div>

        {/* Main content area */}
        <div className="file-attachment-modal-details">
          <img src={iconSrc} alt="File type icon" className="file-attachment-modal-icon" />
          <div className="file-attachment-modal-filename">{filename}</div>
          <div className="file-attachment-modal-file-info">{fileType}</div>
          <div className="file-attachment-modal-file-size">{formattedSize}</div>
        </div>

        {/* Reactions */}
        {reactions && reactions.size > 0 && (
          <div className="file-attachment-modal-reactions">
            <MessageReactions
              reactions={reactions}
              className="in-modal"
              onReactionDetailsOpen={() => setReactionDetailsOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Emoji Picker Portal */}
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="file-attachment-modal-emoji-picker-container"
          onClick={(e) => e.stopPropagation()}
        >
          <SmartEmojiPicker
            messageId={message.getId()}
            reactions={reactions}
            onToggleReaction={onToggleReaction}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
      )}

      {/* Reaction Details Modal Portal */}
      {isReactionDetailsOpen && (
        <ReactionDetailsModal
          reactions={reactions}
          onClose={() => setReactionDetailsOpen(false)}
        />
      )}
    </div>,
    portalContainer,
  );
};

FileAttachmentModal.propTypes = {
  message: PropTypes.object.isRequired,
  senderName: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string,
  filename: PropTypes.string.isRequired,
  msgtype: PropTypes.string.isRequired,
  fileSize: PropTypes.number,
  mimeType: PropTypes.string,
  displayReactions: PropTypes.instanceOf(Map),
  portalContainer: PropTypes.object,
  onReplyMessage: PropTypes.func.isRequired,
  onFocusMessage: PropTypes.func.isRequired,
  onToggleReaction: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FileAttachmentModal;